import {
  AutoNamingErrors,
  AutoNamingRulesForTaskShotQuery,
  AutoNamingRulesForTaskShotQueryVariables,
  CheckVendorDeliveryQuery,
  CheckVendorDeliveryQueryVariables,
  CompanyRole,
  DeliverySourceEnum,
  DeliveryTaskShotInput,
  UpdateVendorDeliveryMutation,
  UpdateVendorDeliveryMutationVariables,
  ValidateDeliveryForAutoNamingMutation,
  ValidateDeliveryForAutoNamingMutationVariables,
  ZippedFilesTaskShotInput,
} from "@/__generated__/graphql";
import Error from "@/components/common/Error";
import {
  bytesToGB,
  createDirectoryStructure,
  Directory,
  findAllFileNames,
  isZipFile,
} from "@/components/common/FileUpload/helpers";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import ServiceDots, {
  getServiceOrSubserviceBasedOnSubservice,
} from "@/components/common/ServiceDots";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserContext } from "@/contexts/UserContextProvider";
import { SubcontractedDeliveriesData } from "@/graphql/fragments";
import {
  getFileNameFromPath,
  getFirstDiffIndex,
  isFile,
} from "@/helpers/filename";
import { PROJECT_COMPLETED_URL } from "@/helpers/url";
import { formatShotWithSubServiceName } from "@/helpers/utils";
import { cn } from "@/lib/utils";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  compact,
  Dictionary,
  every,
  groupBy,
  orderBy,
  uniq,
  uniqBy,
} from "lodash";
import { CircleAlert, RefreshCcw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import JSZip from "jszip";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UploadDeliveryFormValues } from "@/components/Actions/Deliver/UploadDelivery";
import { getOrderedDeliveries } from "@/helpers/taskShotBid";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatDateTimeForDelivery } from "@/helpers/date";
import DeliveryCheckIcon from "@/pages/Assigned/DeliveryCheckIcon";
import DragIcon from "@/assets/DragIcon";

type Props = {
  onNext: (deliveryKeysToTaskShotIds: DeliveryTaskShotInput[]) => void;
  vendorDeliveryId: string;
  uploadDeliveryFormValues?: UploadDeliveryFormValues;
  setSubRuleSelection: (val: Dictionary<string>) => void;
  setDeliveryKeysToTaskShotIds: (val: DeliveryTaskShotInput[]) => void;
  isAutoDeliveringFiles?: boolean;
};

function LinkMedia(props: Props) {
  const { data, loading, error, stopPolling } = useQuery<
    CheckVendorDeliveryQuery,
    CheckVendorDeliveryQueryVariables
  >(CHECK_VENDOR_DELIVERY_QUERY, {
    variables: { id: props.vendorDeliveryId },
    pollInterval: 500,
  });

  useEffect(() => {
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      progressBar.style.visibility = "hidden";
    }
  }, []);

  if (!loading && (error || !data?.vendorDelivery)) {
    return <Error error={`Error: ${error}`} />;
  }

  if (
    !data?.vendorDelivery ||
    data?.vendorDelivery?.status === "PENDING" ||
    loading
  ) {
    return <h2 className="p-6">Scanning directory</h2>;
  }

  stopPolling();

  return <LinkMediaTable vendorDelivery={data.vendorDelivery} {...props} />;
}

function LinkMediaTable({
  vendorDelivery,
  ...props
}: Props & {
  vendorDelivery: NonNullable<CheckVendorDeliveryQuery["vendorDelivery"]>;
}) {
  const { currentProject, isHotspringVendorProfile, role, currentUser } =
    useUserContext();
  const { source, files } = props.uploadDeliveryFormValues || {};
  const navigate = useNavigate();

  const [currentTaskShotBids, setCurrentTaskShotBids] = useState(
    vendorDelivery.taskShotBids,
  );
  const [
    showCustomInstructionWarningModal,
    setShowCustomInstructionWarningModal,
  ] = useState(false);
  const [isScanningFiles, setIsScanningFiles] = useState<boolean>(false);
  const [subrule, setSubrule] = useState<Dictionary<string>>({});
  const [namingErrors, setNamingErrors] = useState<AutoNamingErrors[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { data } = useQuery<
    AutoNamingRulesForTaskShotQuery,
    AutoNamingRulesForTaskShotQueryVariables
  >(AUTO_NAMING_RULES_QUERY, {
    variables: {
      taskShotIds: vendorDelivery.taskShotBids.map((tsb) => tsb.taskShot.id),
    },
    fetchPolicy: "network-only",
  });

  const requiredSubruleSelectionCount =
    data?.autoNamingRulesForTaskShot && !isHotspringVendorProfile
      ? data?.autoNamingRulesForTaskShot
          .filter((ts) => {
            if (ts?.serviceRules && ts?.serviceRules[0]?.subRuleNames?.length) {
              return ts?.serviceRules[0]?.subRuleNames?.length > 1;
            }
            return false;
          })
          .flatMap((cts) =>
            vendorDelivery.taskShotBids
              .map((tsb) => tsb.taskShot)
              .filter((ts) => ts.task.service.id === cts?.task.service.id),
          ).length
      : 0;
  const hasServiceRules = data?.autoNamingRulesForTaskShot?.filter(
    (ts) => ts?.serviceRules?.length,
  ).length;

  const customInstructionTaskShots = currentTaskShotBids
    .map((tsb) => tsb.taskShot)
    .filter(
      (taskShot) =>
        taskShot.task.folderStructureZipUrl || taskShot.task.specDocumentUrl,
    );
  const customInstructions = uniqBy(
    customInstructionTaskShots.map((taskShot) => {
      return {
        service: taskShot.task.service.name,
        specDocUrl: taskShot.task.specDocumentUrl,
        folderStructureZipUrl: taskShot.task.folderStructureZipUrl,
      };
    }),
    (customInstruction) => customInstruction.service,
  );
  const hasCustomInstructions =
    role === CompanyRole.Vendor &&
    (currentUser?.office?.company.profile.isDefault ||
      currentProject?.sourceProject) &&
    customInstructions.length;

  const getInitialState = () => {
    let initialState;
    const deliveredFiles = compact(vendorDelivery.deliveredFiles);
    const directoryStructure = createDirectoryStructure(deliveredFiles);
    const firstLevelElements = directoryStructure.map(
      (entry) => entry.absolutePath,
    );
    const taskShots = vendorDelivery.taskShotBids.map((tsb) => tsb.taskShot);

    if (props.isAutoDeliveringFiles) {
      const newDeliveryKeysToTaskShotIds = taskShots.map((ts) => {
        return getOrderedDeliveries(
          ts.subcontractedDeliveries,
        )[0].deliveryFilesToSourceTaskShotIds.filter(
          (item) => item.taskShotId === ts.id,
        );
      });
      const taskVersions = taskShots.map((ts) => {
        return { taskShotId: ts.id, version: 0 };
      });
      initialState = {
        deliveryKeysToTaskShotIds: newDeliveryKeysToTaskShotIds.flat(),
        additionalMediaKeys: [],
        taskShotDeliveryVersionMap: taskVersions,
        directoryStructure,
      };
    } else {
      initialState = {
        deliveryKeysToTaskShotIds: [],
        additionalMediaKeys:
          source === DeliverySourceEnum.Http
            ? firstLevelElements
            : deliveredFiles,
        directoryStructure,
      };
    }

    return initialState;
  };
  const initialState = getInitialState();
  const [linkedMediaState, setLinkedMediaState] =
    useState<VendorDeliveryConfirmTableState>({
      ...initialState,
    });

  const { deliveryKeysToTaskShotIds, additionalMediaKeys, directoryStructure } =
    linkedMediaState;
  const deliveries = groupBy(deliveryKeysToTaskShotIds, "taskShotId");

  const allDeliveriesLinked = every(
    currentTaskShotBids,
    (tsb) => deliveries[tsb.taskShot.id],
  );

  const getTaskSelectedVersion = (taskShotId: string) => {
    return linkedMediaState.taskShotDeliveryVersionMap?.find(
      (item) => item.taskShotId === taskShotId,
    )?.version;
  };

  const setDeliveryVersion = (taskShotId: string, version: number) => {
    const {
      deliveryKeysToTaskShotIds: previousDeliveryKeysToTaskShotIds,
      taskShotDeliveryVersionMap,
    } = linkedMediaState;
    const taskShot = vendorDelivery.taskShotBids.find(
      (tsb) => tsb.taskShot.id === taskShotId,
    )?.taskShot;

    const newDeliveryKeysToTaskShotIds =
      previousDeliveryKeysToTaskShotIds.filter(
        (item) => item.taskShotId !== taskShotId,
      );

    const newTaskVersion = taskShotDeliveryVersionMap?.filter(
      (item) => item.taskShotId !== taskShotId,
    );

    newTaskVersion?.push({ taskShotId, version });

    const orderedDeliveries = getOrderedDeliveries(
      taskShot?.subcontractedDeliveries || [],
    );

    if (taskShot && orderedDeliveries[version]) {
      newDeliveryKeysToTaskShotIds.push(
        ...orderedDeliveries[version].deliveryFilesToSourceTaskShotIds
          .map((item) => {
            return { ...item, version };
          })
          .filter((item) => item.taskShotId === taskShot.id),
      );
    }

    return setLinkedMediaState((prevState) => {
      return {
        ...prevState,
        deliveryKeysToTaskShotIds: newDeliveryKeysToTaskShotIds,
        taskShotDeliveryVersionMap: newTaskVersion,
      };
    });
  };

  const showToastMessage = (
    taskShotCount: number,
    mappedTaskShotCount: number,
  ) => {
    const unmappedTaskCount = taskShotCount - mappedTaskShotCount;
    if (!unmappedTaskCount && !!mappedTaskShotCount) {
      toast.success(
        `${mappedTaskShotCount} Task${mappedTaskShotCount > 1 ? "s" : ""} linked successfully`,
      );
    } else if (unmappedTaskCount > 0 && !!mappedTaskShotCount) {
      toast.success(
        `${mappedTaskShotCount} Task${
          mappedTaskShotCount > 1 ? "s were" : ""
        } linked successfully. ${unmappedTaskCount} task${
          unmappedTaskCount > 1 ? "s" : ""
        } could not be matched`,
      );
    } else if (unmappedTaskCount === taskShotCount) {
      toast.error(
        `${taskShotCount} Task${taskShotCount > 1 ? "s" : ""} could not be matched`,
      );
    }
  };

  const autoLinkMedia = () => {
    const taskShots = currentTaskShotBids.map((tsb) => tsb.taskShot);
    let newDeliveryKeysToTaskShotIds = [...deliveryKeysToTaskShotIds];
    let newAdditionalKeys = additionalMediaKeys;
    taskShots.forEach((ts) => {
      const fileToAdd = additionalMediaKeys.filter((key) =>
        key.includes(ts.shot.name),
      );
      if (fileToAdd.length === 1) {
        const currentValue = newDeliveryKeysToTaskShotIds.filter(
          (dk) => dk.taskShotId === ts.id,
        );
        if (currentValue.length) {
          newAdditionalKeys.push(currentValue[0].key);
        }
        newDeliveryKeysToTaskShotIds = [
          ...newDeliveryKeysToTaskShotIds.filter(
            (dkt) => dkt.taskShotId !== ts.id,
          ),
          { taskShotId: ts.id, key: fileToAdd[0] },
        ];
        newAdditionalKeys = newAdditionalKeys.filter(
          (tsd) => !tsd.includes(fileToAdd[0]),
        );
      }
    });
    showToastMessage(taskShots.length, newDeliveryKeysToTaskShotIds.length);
    return setLinkedMediaState((prevState) => {
      return {
        ...prevState,
        deliveryKeysToTaskShotIds: newDeliveryKeysToTaskShotIds,
        additionalMediaKeys: newAdditionalKeys,
      };
    });
  };

  const [validateDeliveryForAutoNaming] = useMutation<
    ValidateDeliveryForAutoNamingMutation,
    ValidateDeliveryForAutoNamingMutationVariables
  >(VALIDATE_DELIVERY_FOR_AUTO_NAMING);

  const validateFilesForAutoNaming = async (
    deliveryKeysToTaskShotIds: DeliveryTaskShotInput[],
  ) => {
    if (!files?.length) return;
    const zippedFilesByTaskShotIds: ZippedFilesTaskShotInput[] = [];
    const zippedFilesByTaskShotIdsDict: ZippedFilesTaskShotInputType = {};

    for (const data of deliveryKeysToTaskShotIds) {
      const { taskShotId, key } = data;
      const file = files.filter((_file) => _file.name === key)[0];

      if (!zippedFilesByTaskShotIdsDict[taskShotId])
        zippedFilesByTaskShotIdsDict[taskShotId] = [];

      if (isZipFile(file.name)) {
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(file);
        const zippedFiles = Object.values(zipContent.files)
          .map((value) => value.name)
          .filter(
            (name) =>
              !!isFile(name) &&
              !name.includes("__MACOSX") &&
              !name.includes("DS_Store"),
          );

        zippedFilesByTaskShotIdsDict[taskShotId].push(...zippedFiles);
      } else {
        zippedFilesByTaskShotIdsDict[taskShotId].push(file.name);
      }
    }

    Object.keys(zippedFilesByTaskShotIdsDict).forEach((taskShotId) => {
      zippedFilesByTaskShotIds.push({
        taskShotId,
        zippedFiles: zippedFilesByTaskShotIdsDict[taskShotId],
        subRule: subrule[taskShotId],
      });
    });

    try {
      const result = await validateDeliveryForAutoNaming({
        variables: {
          zippedFilesByTaskShotIds,
          projectId: currentProject?.id as string,
          vendorDeliveryId: vendorDelivery.id,
        },
      });
      if (result && result.data && result.data.validateDeliveryForAutoNaming) {
        const { success, message, autoNamingErrors } =
          result.data.validateDeliveryForAutoNaming;
        if (message && !success) {
          setErrorMessage(message);
        } else if (autoNamingErrors?.length) {
          setNamingErrors(autoNamingErrors);
        } else {
          setIsScanningFiles(false);
          props.onNext(deliveryKeysToTaskShotIds);
        }
      }
    } catch (error) {
      setErrorMessage(String(error));
    }
  };

  const [updateVendorDelivery] = useMutation<
    UpdateVendorDeliveryMutation,
    UpdateVendorDeliveryMutationVariables
  >(UPDATE_VENDOR_DELIVERY);

  const goToNextStep = async (hasServiceRules?: boolean) => {
    await updateVendorDelivery({
      variables: {
        vendorDeliveryId: vendorDelivery.id,
        taskShotBidIds: currentTaskShotBids.map((tsb) => tsb.id),
      },
    });
    if (source === DeliverySourceEnum.Http) {
      const tempDeliveryKeysToTaskShotIds = [...deliveryKeysToTaskShotIds];
      const currentTaskShots = currentTaskShotBids.map(
        (tsb) => tsb.taskShot.id,
      );
      const newTempDeliveryKeysToTaskShotIds =
        tempDeliveryKeysToTaskShotIds.filter((tsObj) =>
          currentTaskShots.includes(tsObj.taskShotId),
        );

      deliveryKeysToTaskShotIds.splice(0, deliveryKeysToTaskShotIds.length);
      newTempDeliveryKeysToTaskShotIds.forEach((item) => {
        directoryStructure.forEach((element) => {
          if (element.absolutePath !== item.key) return true;
          findAllFileNames(element).forEach((fileName) =>
            deliveryKeysToTaskShotIds.push({
              ...item,
              key: fileName,
            }),
          );
          return true;
        });
      });
    }
    props.setDeliveryKeysToTaskShotIds(deliveryKeysToTaskShotIds);

    let fileNames: string[] = [];
    if (source === DeliverySourceEnum.Http) {
      fileNames = deliveryKeysToTaskShotIds.map((item) => item.key);
    }
    const selectedFiles = files?.filter((file) =>
      fileNames.includes(file.name),
    );
    // https://github.com/Stuk/jszip/issues/799
    const shouldScan = selectedFiles?.every((f) => +bytesToGB(f.size) <= 1.9);
    if (
      source === DeliverySourceEnum.Http &&
      currentProject?.sourceProject &&
      hasServiceRules &&
      shouldScan
    ) {
      setIsScanningFiles(true);
      await validateFilesForAutoNaming(deliveryKeysToTaskShotIds);
    } else {
      props.onNext(deliveryKeysToTaskShotIds);
    }
    props.setSubRuleSelection(subrule);
  };

  const showMetadataMismatchWarning = currentTaskShotBids?.some((tsb) => {
    const delivery = orderBy(
      tsb.taskShot.subcontractedDeliveries,
      (v) => v?.created,
      "desc",
    )[0];
    if (!delivery || !delivery.metadataMismatches) return false;
    const metadataMismatches = delivery.metadataMismatches.find(
      (mm) => mm.taskShotId === tsb.taskShot.id,
    )?.mismatches;
    return !!(metadataMismatches && metadataMismatches.length > 0);
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="p-6 flex bg-quinary">
        <div className="flex-3 pr-6">
          <Table className="table-fixed !border-spacing-x-6 !border-spacing-y-4 border-separate">
            <TableHeader className="text-base font-medium text-secondary-foreground sticky top-0 bg-transparent">
              <TableRow className="bg-transparent hover:bg-transparent border-none [&_th]:py-3 [&_th]:px-0">
                <TableHead className="">Shot Name</TableHead>
                <TableHead className="w-35">Service</TableHead>
                {!isHotspringVendorProfile &&
                  !!data?.autoNamingRulesForTaskShot
                    ?.filter((subRule) => !!subRule?.serviceRules?.length)
                    .filter(
                      (sb) =>
                        sb?.serviceRules &&
                        sb.serviceRules[0] &&
                        sb.serviceRules[0].subRuleNames &&
                        sb.serviceRules[0].subRuleNames?.length > 1,
                    )?.length && (
                    <TableHead className="w-35">Select Subrule</TableHead>
                  )}
                <TableHead className="">Linked Media</TableHead>
                {props.isAutoDeliveringFiles && (
                  <TableHead className="w-50">Vendor</TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody className="text-sm text-primary-foreground">
              {currentTaskShotBids.map((tsb) => {
                const serviceName = tsb.taskShot.task.service.name;
                const shotName = formatShotWithSubServiceName(
                  tsb.taskShot.shot.name,
                  tsb.taskShot.subService,
                );
                const deliveriesForTaskShot = (deliveries[tsb.taskShot.id] ||
                  []) as DeliveryTaskShotInput[];
                const subcontractedDeliveries = getOrderedDeliveries(
                  tsb.taskShot.subcontractedDeliveries || [],
                );
                const vendorName =
                  subcontractedDeliveries.length &&
                  subcontractedDeliveries[0].vendor.profile.companyName;

                if (deliveriesForTaskShot.some((delivery) => delivery.isBroken))
                  return null;

                const serviceRuledata = data?.autoNamingRulesForTaskShot?.find(
                  (rule) => rule?.task.service.name === serviceName,
                );

                const hasMetadataMismatch = subcontractedDeliveries.some(
                  (delivery) => {
                    const metadataMismatches = delivery.metadataMismatches.find(
                      (mm) => mm.taskShotId === tsb.taskShot.id,
                    )?.mismatches;
                    return !!(
                      metadataMismatches && metadataMismatches.length > 0
                    );
                  },
                );

                return (
                  <TableRow
                    className={cn(
                      "border-none bg-transparent hover:bg-transparent [&_td]:p-0",
                    )}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const mediaKey = e.dataTransfer.getData("text/plain");
                      if (additionalMediaKeys.includes(mediaKey)) {
                        setLinkedMediaState({
                          ...linkedMediaState,
                          deliveryKeysToTaskShotIds: uniq([
                            ...deliveryKeysToTaskShotIds,
                            {
                              key: mediaKey,
                              taskShotId: tsb.taskShot.id,
                            },
                          ]),
                          additionalMediaKeys: additionalMediaKeys.filter(
                            (key) => key !== mediaKey,
                          ),
                        });
                      }
                    }}
                  >
                    <TableCell className="">
                      <div className="flex justify-between items-center">
                        <div className="border px-4 py-3 w-[85%] rounded-md flex justify-between items-center">
                          <LongContentWrapper>{shotName}</LongContentWrapper>
                          {hasMetadataMismatch && (
                            <DeliveryCheckIcon
                              taskShotId={tsb.taskShot.id}
                              deliveries={tsb.taskShot.subcontractedDeliveries}
                            />
                          )}
                        </div>

                        <Button
                          className="border p-2!"
                          onClick={() => {
                            const newTsbs = currentTaskShotBids.filter(
                              (ctsb) => ctsb.taskShot.id !== tsb.taskShot.id,
                            );
                            setCurrentTaskShotBids(newTsbs);
                          }}
                        >
                          <X />
                        </Button>
                      </div>
                    </TableCell>

                    <TableCell>
                      <ServiceDots
                        transparent
                        colorBorder
                        square
                        colorText
                        services={getServiceOrSubserviceBasedOnSubservice(
                          [tsb.taskShot.task.service],
                          tsb.taskShot.subService,
                        )}
                        innerClassName="w-full !py-2 text-[16px]"
                      />
                    </TableCell>

                    {!isHotspringVendorProfile &&
                      !!serviceRuledata?.serviceRules?.length &&
                      serviceRuledata?.serviceRules &&
                      serviceRuledata?.serviceRules[0]?.subRuleNames &&
                      serviceRuledata?.serviceRules[0]?.subRuleNames?.length >
                        1 && (
                        <TableCell>
                          <Select
                            onValueChange={(val) => {
                              setSubrule((prev) => {
                                const currentObj = { ...prev };
                                currentObj[tsb.taskShot.id] = val;
                                return currentObj;
                              });
                            }}
                          >
                            <SelectTrigger className="w-30 border-none !bg-secondary">
                              <SelectValue placeholder="Select subrule" />
                            </SelectTrigger>

                            <SelectContent className="">
                              <SelectGroup>
                                {serviceRuledata?.serviceRules &&
                                  serviceRuledata.serviceRules[0]?.subRuleNames?.map(
                                    (subRuleName) => (
                                      <SelectItem
                                        value={subRuleName}
                                        key={subRuleName}
                                      >
                                        {subRuleName}
                                      </SelectItem>
                                    ),
                                  )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      )}

                    <TableCell>
                      <div className="max-h-50 overflow-y-scroll">
                        <div className="flex flex-col gap-5">
                          {!deliveriesForTaskShot.length && (
                            <div className="text-secondary-foreground text-xs font-light px-4 py-3 border rounded-md">
                              Drag media here to assign
                            </div>
                          )}

                          {deliveriesForTaskShot.map((delivery) => (
                            <div className="flex justify-between items-center">
                              <div className="border px-4 py-3 w-[85%] rounded-md">
                                <LongContentWrapper>
                                  {(() => {
                                    const fileName = getFileNameFromPath(
                                      delivery.key,
                                    );
                                    const diffIndex: number = getFirstDiffIndex(
                                      fileName,
                                      shotName,
                                    );

                                    const split1 = fileName.slice(0, diffIndex);
                                    const split2 = fileName.slice(diffIndex);

                                    return (
                                      <span>
                                        {split1.length > 0 && (
                                          <span>{split1}</span>
                                        )}
                                        {split2.length > 0 && (
                                          <span className="bg-[#ffa9a9]">
                                            {split2}
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })()}
                                </LongContentWrapper>
                              </div>

                              <Button
                                className={cn(
                                  "border p-2!",
                                  props.isAutoDeliveringFiles && "hidden",
                                )}
                                onClick={() => {
                                  setLinkedMediaState({
                                    ...linkedMediaState,
                                    deliveryKeysToTaskShotIds:
                                      linkedMediaState.deliveryKeysToTaskShotIds.filter(
                                        (d) => d.key !== delivery.key,
                                      ),
                                    additionalMediaKeys: uniq([
                                      ...additionalMediaKeys,
                                      delivery.key,
                                    ]),
                                  });
                                }}
                              >
                                <X />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>

                    {props.isAutoDeliveringFiles &&
                      !!subcontractedDeliveries.length && (
                        <TableCell className="">
                          <div className="flex justify-between items-center">
                            <div className="border border-destructive text-destructive px-4 py-3 w-[85%] rounded-md">
                              <LongContentWrapper>
                                {vendorName}
                              </LongContentWrapper>
                            </div>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button className="border p-2! text-destructive mx-2">
                                  <CircleAlert />
                                </Button>
                              </TooltipTrigger>

                              <TooltipContent
                                className="p-6 border-2"
                                side="left"
                              >
                                <div className="text-base font-medium">
                                  {shotName}
                                </div>
                                <Separator className="my-4" />
                                <div className="text-sm font-medium text-center text-secondary-foreground my-4">
                                  This task has {subcontractedDeliveries.length}{" "}
                                  associated packages from {vendorName}
                                </div>

                                <div className="max-h-100 overflow-y-scroll">
                                  <div className="flex flex-col gap-2">
                                    {subcontractedDeliveries.map(
                                      (delivery, idx) => (
                                        <RadioGroup>
                                          <div
                                            className="p-4 bg-secondary rounded-md flex flex-col gap-2 cursor-pointer"
                                            onClick={() => {
                                              setDeliveryVersion(
                                                tsb.taskShot.id,
                                                idx,
                                              );
                                            }}
                                          >
                                            <div className="flex items-center justify-between gap-6">
                                              <div className="flex flex-col gap-1 font-medium text-sm">
                                                {delivery.deliveryFilesToSourceTaskShotIds
                                                  .filter(
                                                    (v) =>
                                                      v.taskShotId ===
                                                      tsb.taskShot.id,
                                                  )
                                                  .map((item) => (
                                                    <LongContentWrapper>
                                                      {getFileNameFromPath(
                                                        item.key,
                                                      )}
                                                    </LongContentWrapper>
                                                  ))}
                                              </div>

                                              <RadioGroupItem
                                                checked={
                                                  idx ===
                                                  getTaskSelectedVersion(
                                                    tsb.taskShot.id,
                                                  )
                                                }
                                                value={idx.toString()}
                                              />
                                            </div>

                                            <div className="flex items-center justify-between gap-6 font-light text-xs">
                                              <div>
                                                <span>
                                                  Delivered on &nbsp;
                                                  {formatDateTimeForDelivery(
                                                    delivery.created,
                                                    delivery.creator.timezone,
                                                  )}{" "}
                                                  by &nbsp;
                                                  {vendorName}
                                                </span>
                                              </div>

                                              {idx === 0 && (
                                                <div className="px-4 py-1 bg-yellow-600 rounded-full font-bold">
                                                  Latest
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </RadioGroup>
                                      ),
                                    )}
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {!props.isAutoDeliveringFiles && (
          <div className="flex-1 pl-6 border-l flex flex-col gap-5">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-secondary-foreground font-medium">
                  Unlinked Media
                </div>
                <div className="text-secondary-foreground text-xs font-light mt-1">
                  Drag and drop media to assign to shot
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  className="text-service-paint hover:bg-transparent bg-transparent hover:text-service-paint/80 p-0"
                  onClick={autoLinkMedia}
                >
                  <RefreshCcw />
                  Auto link
                </Button>

                <Button
                  className="text-link hover:bg-transparent bg-transparent hover:text-link/80 p-0"
                  onClick={() => {
                    setCurrentTaskShotBids(vendorDelivery.taskShotBids);
                    setLinkedMediaState({
                      deliveryKeysToTaskShotIds: [],
                      additionalMediaKeys: compact(
                        vendorDelivery.deliveredFiles,
                      ),
                      directoryStructure: createDirectoryStructure(
                        compact(vendorDelivery.deliveredFiles),
                      ),
                    });
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>

            <div className="overflow-scroll max-h-80">
              <div className="flex flex-col gap-5">
                {linkedMediaState.additionalMediaKeys.sort().map((key) => {
                  return (
                    <div
                      className="border text-sm flex gap-2 items-center px-4 py-3 rounded-md max-w-100 cursor-grab hover:bg-secondary"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", key);
                        e.dataTransfer.dropEffect = "move";
                        setTimeout(() => {
                          const ele = e.target as HTMLElement;
                          ele.style.opacity = "0.3";
                        }, 0);
                      }}
                      onDragEnd={(e) => {
                        const ele = e.target as HTMLElement;
                        ele.style.opacity = "1";
                      }}
                    >
                      <DragIcon
                        color="var(--color-primary-foreground)"
                        className="shrink-0"
                      />
                      <LongContentWrapper>
                        {getFileNameFromPath(key)}
                      </LongContentWrapper>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-primary border-t flex justify-end gap-4 py-4 px-6 items-center">
        {!!namingErrors.length && (
          <Error
            error={
              "We detected following issues in the delivery package. Please resolve the issues and redeliver the package"
            }
            errors={namingErrors
              .map(
                (e) =>
                  e.errors?.map((error) =>
                    error.concat(` for ${e.taskShotName}`),
                  ) || null,
              )
              .flat()}
          />
        )}

        <Error error={errorMessage} />

        {showMetadataMismatchWarning && (
          <p className="text-sm text-red-500">
            Some selected tasks have either&nbsp;
            <strong>failed or are pending Tech Checks.</strong>
            &nbsp;Delivering them may result in quality issues or client
            rejection. Click "Next" to proceed or "Back" to recheck the
            deliveries
          </p>
        )}

        <Button
          onClick={() => {
            if (currentProject)
              navigate(
                generatePath(PROJECT_COMPLETED_URL, {
                  project_id: currentProject.id,
                }),
              );
          }}
        >
          Back
        </Button>

        <Button
          onClick={() => {
            goToNextStep(!!hasServiceRules);
          }}
          disabled={
            isScanningFiles ||
            !allDeliveriesLinked ||
            Object.keys(subrule).length !== requiredSubruleSelectionCount
          }
          className="bg-service-paint hover:bg-service-paint/96 text-white"
        >
          Next
        </Button>
      </div>

      {showCustomInstructionWarningModal && hasCustomInstructions && (
        <Dialog
          open
          onOpenChange={(open) =>
            !open && setShowCustomInstructionWarningModal(false)
          }
        >
          <DialogContent>
            <div className="font-medium text-lg">Warning!</div>
            <Separator />
            <div>
              You are delivering tasks that have custom Instructions. Please
              ensure you have followed them. Spec Documents:
            </div>
            {customInstructions.map((instruction) => {
              return (
                <div className="text-sm [&_a]:text-service-paint">
                  <p className="font-medium">{instruction.service} :</p>
                  <div>
                    {instruction.specDocUrl && (
                      <a href={instruction.specDocUrl}>
                        Specification Document
                      </a>
                    )}
                    {instruction.folderStructureZipUrl && (
                      <a href={instruction.folderStructureZipUrl}>
                        Delivery Folder Structure
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            <Separator />
            <Button
              className="bg-service-paint hover:bg-service-paint/96 text-white"
              onClick={() => {
                if (hasCustomInstructions)
                  setShowCustomInstructionWarningModal(true);
                else goToNextStep(!!hasServiceRules);
              }}
            >
              Confirm
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default LinkMedia;

interface VendorDeliveryConfirmTableState {
  deliveryKeysToTaskShotIds: DeliveryTaskShotInput[];
  additionalMediaKeys: string[];
  taskShotDeliveryVersionMap?: TaskShotDeliveryVersion[];
  directoryStructure: Directory[];
}

interface TaskShotDeliveryVersion {
  taskShotId: string;
  version: number;
}

interface ZippedFilesTaskShotInputType {
  [key: string]: string[];
}

export const CHECK_VENDOR_DELIVERY_QUERY = gql`
  query CheckVendorDelivery($id: SmartID!) {
    vendorDelivery(id: $id) {
      id
      status
      deliveredFiles
      taskShotBids {
        id
        status
        taskShot {
          id
          subcontractedDeliveries {
            ...SubcontractedDeliveriesData
          }
          subService {
            id
            name
          }
          task {
            id
            service {
              id
              name
            }
            specDocumentUrl
            folderStructureZipUrl
          }
          shot {
            id
            name
          }
        }
      }
    }
  }
  ${SubcontractedDeliveriesData}
`;

export const AUTO_NAMING_RULES_QUERY = gql`
  query AutoNamingRulesForTaskShot($taskShotIds: [SmartID]) {
    autoNamingRulesForTaskShot(taskShotIds: $taskShotIds) {
      id
      task {
        id
        service {
          id
          name
        }
        creator {
          id
          office {
            id
            name
          }
        }
        project {
          id
          name
        }
      }
      serviceRules {
        id
        isDefault
        mandatorySequencesDesc {
          name
          description
          allowedFormats
        }
        mandatoryFormats {
          format
          subFormat {
            name
            mandatory
            description
          }
          count
          mandatory
          description
        }
        nonMandatorySequencesDesc {
          name
          description
          allowedFormats
        }
        nonMandatoryFormats {
          format
          subFormat {
            name
            mandatory
            description
          }
          count
          mandatory
          description
        }
        rules {
          name
          mandatoryFormats {
            format
            subFormat {
              name
              mandatory
              description
            }
            count
            mandatory
            description
          }
          nonMandatoryFormats {
            format
            subFormat {
              name
              mandatory
              description
            }
            count
            mandatory
            description
          }
          mandatorySequences {
            name
            description
            allowedFormats
          }
          nonMandatorySequences {
            name
            description
            allowedFormats
          }
        }
        subRuleNames
      }
    }
  }
`;

export const UPDATE_VENDOR_DELIVERY = gql`
  mutation UpdateVendorDelivery(
    $vendorDeliveryId: SmartID!
    $taskShotBidIds: [SmartID]!
  ) {
    updateVendorDelivery(
      taskShotBidIds: $taskShotBidIds
      vendorDeliveryId: $vendorDeliveryId
    ) {
      success
      message
    }
  }
`;

export const VALIDATE_DELIVERY_FOR_AUTO_NAMING = gql`
  mutation ValidateDeliveryForAutoNaming(
    $projectId: SmartID!
    $vendorDeliveryId: SmartID!
    $zippedFilesByTaskShotIds: [ZippedFilesTaskShotInput]!
  ) {
    validateDeliveryForAutoNaming(
      projectId: $projectId
      vendorDeliveryId: $vendorDeliveryId
      zippedFilesByTaskShotIds: $zippedFilesByTaskShotIds
    ) {
      success
      message
      autoNamingErrors {
        taskShotName
        errors
      }
    }
  }
`;
